import React, { useContext, useReducer, useState, useEffect } from 'react';
import { Checkbox } from '@paycor/picl/form-elements';
import { isEqual } from 'lodash';
import { ToggleSwitch } from '@paycor/form-elements';
import { InputField } from '@paycor/picl/form-elements';
import ToolTip from '@paycor/tooltip';
import { Info, Check } from '@paycor/icon';
import { Colors } from '@paycor/core';
import Banner from '@paycor/picl/banner';
import Button from '@paycor/picl/button';
import {
	getHasNewEmployeeSettingsPageEnabled,
	loadCompanyDetails,
	loadEmployeeNumberSettings,
	loadHasBSwiftService,
	loadHasPendingHiresWithEmployeeNumber,
	saveEmployeeNumberSettings
} from 'services/employeeService';
import { loadClientPrivilege } from 'services/securityService';
import { employeeSettingsActions, employeeSettingsReducer } from 'reducers/employeeSettingsReducer';
import { UserPrivContext } from 'components/Security/AuthorizeUserPrivilege';
import style from './style.module.scss';
import config from 'config';
import { scrollToTop } from 'utils/helpers';
import { Privs, clearBanner } from 'constants';
import { useParams } from 'react-router-dom';
import { t } from 'i18next';
import LoadingSplashScreen from 'components/LoadingSplashScreen/LoadingSplashScreen';
import { IntegrationConsiderationsModal } from './IntegrationConsiderationModal';

const {
	HOST: { SECURE_BASE_DOMAIN_URL }
} = config;

const initialState = {
	settings: {},
	savedSettings: {},
	errors: {}
};

const EmployeeNumberSettings = ({ onIsDirtyChange }) => {
	const { companyId, clientId } = useParams();
	const { hasEditPriv } = useContext(UserPrivContext);
	const [_employeeSettings, dispatchEmployeeSettings] = useReducer(employeeSettingsReducer, initialState);
	const [isPageLoading, setIsPageLoading] = useState(true);
	const [isDirty, setDirty] = useState(false);
	const [bannerMessage, setBannerMessage] = useState({ clearBanner });
	const [showModal, setShowModal] = useState(false);
	const [excludeSameEmployeeNumberFeature, setExcludeSameEmployeeNumberFeature] = useState(false);

	useEffect(() => {
		const initializeAppEffect = async () => {
			try {
				const hasNewEmployeeSettingsPageEnabled = await getHasNewEmployeeSettingsPageEnabled(companyId);

				if (hasNewEmployeeSettingsPageEnabled) {
					const [companyDetails, employeeSettings, hasPendingHiresWithEmployeeNumber, hasBSwiftService, clientPrivilege] = await Promise.all([
						loadCompanyDetails(companyId, clientId),
						loadEmployeeNumberSettings(companyId),
						loadHasPendingHiresWithEmployeeNumber(companyId),
						loadHasBSwiftService(companyId, clientId),
						loadClientPrivilege(companyId, Privs.EXCLUDE_SAME_EMPLOYEENUMBER_FEATURE)
					]);

					dispatchEmployeeSettings({
						type: employeeSettingsActions.INITIALIZE,
						payload: {
							value: {
								...employeeSettings,
								employeeNumberSeed: employeeSettings.employeeNumberSeed?.toString(),
								allowAlphaNumericEmployeeNumber: employeeSettings.allowAlphaNumericEmployeeNumber ?? false,
								allowLeadingZeros: employeeSettings.allowLeadingZeros ?? false,
								employeeNumberMaxLength: employeeSettings.employeeNumberMaxLength ?? 6,
								hasPendingHiresWithEmployeeNumber: hasPendingHiresWithEmployeeNumber ?? false,
								hasBSwiftService: hasBSwiftService ?? false,
								companyName: `${companyDetails.companyId} - ${companyDetails.companyName}`
							}
						}
					});
					setExcludeSameEmployeeNumberFeature(Object.values(clientPrivilege).some((value) => value === true));

				} else {
					window.location.href = `${SECURE_BASE_DOMAIN_URL}/Perform/Company#${clientId}/HrSettings?useLegacyHrSettings=true`;
				}
			} catch (error) {
				console.log(error);
				setBannerMessage({
					showBanner: true,
					text: t(`Unable_to_load_employee_settings`, `Sorry, we can’t load Employee Settings right now. Please wait a bit and try again.`),
					status: Banner.Status.ERROR
				});
			} finally {
				setIsPageLoading(false);
			}
		};
		initializeAppEffect();
	}, [companyId, clientId]);

	useEffect(() => {
		isEqual(_employeeSettings.settings, _employeeSettings.savedSettings) ? setDirty(false) : setDirty(true);
	}, [_employeeSettings]);

	const handleChange = ({ type, key, value }) => {
		dispatchEmployeeSettings({ type, payload: { key, value } });
	};

	const horizontalSeparator = () => {
		return (
			<>
				<hr className={style.horizontalline} />
			</>
		);
	};

	useEffect(() => {
		onIsDirtyChange(isDirty);
	}, [isDirty, onIsDirtyChange]);

	const handleShowModal = (showModal) => {
		setShowModal(showModal);
	};

	const handleSubmit = () => {
		const { useFirstAvailableEmployeeNumber, employeeNumberSeed } = _employeeSettings.settings;
		const { employeeNumberMaxLength } = _employeeSettings.settings;
		scrollToTop();
		const isFormattingOptionsChanged =
			_employeeSettings.settings.allowAlphaNumericEmployeeNumber !== _employeeSettings.savedSettings.allowAlphaNumericEmployeeNumber ||
			_employeeSettings.settings.allowLeadingZeros !== _employeeSettings.savedSettings.allowLeadingZeros ||
			_employeeSettings.settings.employeeNumberMaxLength !== _employeeSettings.savedSettings.employeeNumberMaxLength;

		if (
			(employeeNumberMaxLength < 6 || employeeNumberMaxLength > 10) &&
			useFirstAvailableEmployeeNumber &&
			(!employeeNumberSeed || employeeNumberSeed < 1 || employeeNumberSeed.length > _employeeSettings.settings.employeeNumberMaxLength)
		) {
			const maxEmployeeNumber = '9'.repeat(_employeeSettings.settings.employeeNumberMaxLength);
			let text = (
				<div>
					<div style={{ marginBottom: 10 }}>{t('changes_not_saved', 'Your changes were not saved. Please correct the errors below.')}</div>
					<li>{t('Employee_Number_Seed_Warning_Message', 'The starting employee number must be between 1 to') + ` ${maxEmployeeNumber}.`}</li>
					<li>{t('Employee_Number_Max_Length_Warning_Message', 'The Maximum Character Length of Employee Number must be between 6 and 30.')}</li>
				</div>
			);
			setBannerMessage({
				showBanner: true,
				text,
				status: Banner.Status.ERROR
			});
			handleShowModal(false);
			dispatchEmployeeSettings({
				type: employeeSettingsActions.ERROR,
				payload: {
					employeeNumberSeed: t('Employee_Number_Seed_Warning_Message', 'The starting employee number must be between 1 to') + ` ${maxEmployeeNumber}.`,
					employeeNumberMaxLength: t('Employee_Number_Max_Length_Warning_Message', 'The Maximum Character Length of Employee Number must be between 6 and 10.')
				}
			});
			return;
		} else if (employeeNumberMaxLength < 6 || employeeNumberMaxLength > 10) {
			let text = (
				<div>
					<div style={{ marginBottom: 10 }}>{t('changes_not_saved', 'Your changes were not saved. Please correct the errors below.')}</div>
					<li>{t('Employee_Number_Max_Length_Warning_Message', 'The Maximum Character Length of Employee Number must be between 6 and 10.')}</li>
				</div>
			);

			setBannerMessage({
				showBanner: true,
				text,
				status: Banner.Status.ERROR
			});
			handleShowModal(false);
			dispatchEmployeeSettings({
				type: employeeSettingsActions.ERROR,
				payload: { employeeNumberMaxLength: t('Employee_Number_Max_Length_Warning_Message', 'The Maximum Character Length of Employee Number must be between 6 and 10.') }
			});
			return;
		} else if (
			useFirstAvailableEmployeeNumber &&
			(!employeeNumberSeed || employeeNumberSeed < 1 || employeeNumberSeed.length > _employeeSettings.settings.employeeNumberMaxLength)
		) {
			const maxEmployeeNumber = '9'.repeat(_employeeSettings.settings.employeeNumberMaxLength);
			let text = (
				<div>
					<div style={{ marginBottom: 10 }}>{t('changes_not_saved', 'Your changes were not saved. Please correct the errors below.')}</div>
					<li>{t('Employee_Number_Seed_Warning_Message', 'The starting employee number must be between 1 to') + ` ${maxEmployeeNumber}.`}</li>
				</div>
			);
			setBannerMessage({
				showBanner: true,
				text,
				status: Banner.Status.ERROR
			});
			handleShowModal(false);
			dispatchEmployeeSettings({
				type: employeeSettingsActions.ERROR,
				payload: { employeeNumberSeed: t('Employee_Number_Seed_Warning_Message', 'The starting employee number must be between 1 to') + ` ${maxEmployeeNumber}.` }
			});
			return;
		} else if (isFormattingOptionsChanged) {
			handleShowModal(true);
			return;
		} else {
			dispatchEmployeeSettings({
				type: employeeSettingsActions.ERROR,
				payload: {}
			});
		}
		if (_employeeSettings.settings.hasBSwiftService && !_employeeSettings.settings.companyLevelEmployeeNumber) {
			_employeeSettings.settings.companyLevelEmployeeNumber = true;
		}
		if (!_employeeSettings.settings.companyLevelEmployeeNumber) {
			_employeeSettings.settings.useFirstAvailableEmployeeNumber = false;
			_employeeSettings.settings.enableSameEmployeeNumberForLinkedEmployees = false;
		}
		if (!_employeeSettings.settings.useFirstAvailableEmployeeNumber) {
			_employeeSettings.settings.employeeNumberSeed = null;
		}
		handleAPI();

		setDirty(false);
	};

	const handleAPI = async () => {
		setIsPageLoading(true);
		try {
			const response = await saveEmployeeNumberSettings(_employeeSettings.settings);
			setBannerMessage({
				showBanner: true,
				text: t('save_message', 'Success! Your changes were saved.'),
				status: Banner.Status.SUCCESS
			});
			setTimeout(() => {
				setBannerMessage({ clearBanner });
			}, 5000);
			dispatchEmployeeSettings({
				type: employeeSettingsActions.INITIALIZE,
				payload: { value: { ...response, companyName: _employeeSettings.savedSettings.companyName, hasBSwiftService: _employeeSettings.settings.hasBSwiftService } }
			});
			dispatchEmployeeSettings({
				type: employeeSettingsActions.ERROR,
				payload: {}
			});
		} catch (err) {
			console.error(err);
			if (err.name === 'ValidationError') {
				let text = (
					<div>
						<div style={{ marginBottom: 10 }}>{t('changes_not_saved', 'Your changes were not saved. Please correct the errors below.')}</div>
						<li>{err.message._error}</li>
					</div>
				);
				setBannerMessage({
					showBanner: true,
					text,
					status: Banner.Status.ERROR
				});
			} else {
				setBannerMessage({
					showBanner: true,
					text: t('unable_to_save_message', 'Sorry, we can’t save Employee Settings right now. Please wait a bit and try again.'),
					status: Banner.Status.ERROR
				});
			}
		}
		setIsPageLoading(false);
	};

	const handleCancel = () => {
		setDirty(false);
		dispatchEmployeeSettings({ type: employeeSettingsActions.RESET });
		setBannerMessage(clearBanner);
	};

	const renderBanner = () => {
		return (
			<div className={bannerMessage.showBanner ? style.banner_wrapper : ''}>
				<Banner
					id={'banner'}
					message={bannerMessage.showBanner ? bannerMessage.text : null}
					status={bannerMessage.status ? bannerMessage.status : Banner.Status.SUCCESS}
					show={bannerMessage.showBanner}
					onClose={() => {
						setBannerMessage({ ...bannerMessage, showBanner: !bannerMessage.showBanner });
					}}
					onToggle={() => {
						setBannerMessage({ ...bannerMessage, showBanner: !bannerMessage.showBanner });
					}}
				/>
			</div>
		);
	};

	const renderEmployeeNumbering = () => {
		return (
			<>
				<section className={style.section_container}>
					<span className={style.section_title}>{t('IncrementationSetting', 'Incrementation Setting')}</span>
					<span className={style.description}>
						{_employeeSettings.settings.hasBSwiftService ? (
							<span>{t('CompanyLevelEmployeeNumberRequiredForBenefitAdvisor', 'Required when using Benefits Advisor')}</span>
						) : (
							<>
								<span>{t('incrementation_setting_note', 'Please resolve all duplicate employee numbers before activating this setting.')}</span>
							</>
						)}
					</span>
					<div>
						<span className={style.toggle_container}>
							<ToggleSwitch
								toggleState={_employeeSettings.settings.hasBSwiftService ? _employeeSettings.settings.hasBSwiftService : _employeeSettings.settings.companyLevelEmployeeNumber}
								onLabel={'On'}
								offLabel={'Off'}
								onChange={(e) => handleChange({ type: employeeSettingsActions.HANDLE_TOGGLE, key: 'companyLevelEmployeeNumber', value: e })}
								isDisabled={_employeeSettings.settings.hasBSwiftService}
							/>
							<span>
								{t('employee_number_off', 'When off, the system will assign the next highest available Employee Number within your')} <strong>{t('client_id', 'Client ID')}</strong>{' '}
								<br />
								{t('employee_number_on', 'When on, if you manage multiple clients, the system will assign new hires the next highest available Employee Number within your')}{' '}
								<strong>{t('company_id', 'Company ID')}</strong>
								<span className={style.gap}>
									<ToolTip position={ToolTip.Positions.TOP}>
										<ToolTip.Anchor>
											<Info color={Colors.BLUE2} />
										</ToolTip.Anchor>
										<ToolTip.Body>
											<div className={style.message_width}>
												{t(
													'employee_number_range_tooltip_message',
													"Example: If Client A's employee numbers range from 1 to 50 and Client B's employee numbers range from 500 to 575, the company's next employee would be assigned number 576."
												)}
											</div>
										</ToolTip.Body>
									</ToolTip>
								</span>
							</span>
						</span>
						<div className={style.checkbox_container}>
							{(_employeeSettings.settings.hasBSwiftService || _employeeSettings.settings.companyLevelEmployeeNumber) && (
								<>
									<div>
										<span className={style.employee_number}>
											<Checkbox
												id={'useFirstAvailableEmployeeNumber'}
												checkedStatus={_employeeSettings.settings.useFirstAvailableEmployeeNumber ? Checkbox.StatusTypes.CHECKED : Checkbox.StatusTypes.NONE}
												onChange={(e) => handleChange({ type: employeeSettingsActions.HANDLE_TOGGLE, key: 'useFirstAvailableEmployeeNumber', value: e.checked })}
												label={t(
													'Allow_custom_Employee_Numbering',
													'Allow custom incrementation within the Company (only applies to employee numbers without letters or leading zeros)'
												)}
											/>
											<p className={`${style.required_desc} ${style.gap}`}>
												<ToolTip position={ToolTip.Positions.TOP}>
													<ToolTip.Anchor>
														<Info color={Colors.BLUE2} />
													</ToolTip.Anchor>
													<ToolTip.Body>
														<div className={style.message_width}>
															{t(
																'Use_custom_Employee_Numbering_on_tooltip',
																"When selected, we'll assign the next available employee number within a company, starting at the number added below."
															)}
															<br />
															<br />{' '}
															{t(
																'Use_custom_Employee_Numbering_start_number_tooltip',
																"Example: If the company has a starting number of 1 and Client A's employee numbers range from 1-50 and Client B's employee numbers range from 500-575, the company's next employee would be assigned number 51."
															)}
														</div>
													</ToolTip.Body>
												</ToolTip>
											</p>
										</span>
										<>
											{_employeeSettings.settings.useFirstAvailableEmployeeNumber && (
												<span className={style.employee_numbering_input_wrapper}>
													<div className={style.input_container}>
														<InputField
															id={'employeeNumberSeed'}
															label={t('employee_number_incrementation', 'Enter the number incrementation will start at')}
															value={_employeeSettings.settings.employeeNumberSeed ? String(_employeeSettings.settings.employeeNumberSeed) : ''}
															errorMessage={_employeeSettings.errors.employeeNumberSeed ? ' ' : ''}
															onChange={(e) => handleChange({ type: employeeSettingsActions.HANDLE_TOGGLE, key: 'employeeNumberSeed', value: e })}
															isRequired={true}
															characterLimit={10}
														/>

														{_employeeSettings.errors.employeeNumberSeed && <div className={style.error_message}>{_employeeSettings.errors.employeeNumberSeed}</div>}
													</div>
												</span>
											)}
										</>
									</div>

									<span className={`${style.toggle_container} ${style.checkbox_setting}`}>
										<Checkbox
											checkedStatus={excludeSameEmployeeNumberFeature 
												? Checkbox.StatusTypes.NONE 
												: (_employeeSettings.settings.enableSameEmployeeNumberForLinkedEmployees 
													? Checkbox.StatusTypes.CHECKED 
													: Checkbox.StatusTypes.NONE)}
											onChange={(e) => handleChange({ type: employeeSettingsActions.HANDLE_TOGGLE, key: 'enableSameEmployeeNumberForLinkedEmployees', value: e.checked })}
											label={t('Use_the_same_Employee_Number_for_linked_employees', 'Use the same Employee Number for linked employees') + 
												(excludeSameEmployeeNumberFeature ? ' (' + t('Exclude_Same_EmployeeNumber_Feature', 'your company has unsupported services that cannot utilize this setting') + ')' : '')}
											isDisabled={excludeSameEmployeeNumberFeature}
										/>
										<span>
											<ToolTip position={ToolTip.Positions.TOP}>
												<ToolTip.Anchor>
													<Info color={Colors.BLUE2} />
												</ToolTip.Anchor>
												<ToolTip.Body>
													<div className={style.message_width}>
														{t(
															'Use_same_Employee_Number_for_linked_employees_tooltip_message',
															"When selected, a linked employee's number on one profile can be set to the same on other profiles, in different Clients. Make updates on the employee's Status page or during a transfer. Note that this feature may not apply to companies with unsupported services. Please refer to our Knowledge Base under Employee Settings for more information."
														)}
													</div>
												</ToolTip.Body>
											</ToolTip>
										</span>
									</span>
								</>
							)}
						</div>
					</div>
				</section>
				{horizontalSeparator()}
			</>
		);
	};

	const renderEmployeeNumberCreationPreference = () => {
		const { hasPendingHiresWithEmployeeNumber, enableEmployeeNumberCreationBeforeHire } = _employeeSettings.settings;
		const enableEmployeeNumberCreationDuringNewHireWarning = hasPendingHiresWithEmployeeNumber === true && enableEmployeeNumberCreationBeforeHire === true;

		return (
			<>
				<section className={style.section_container}>
					<span className={style.section_title}>{t('employee_number_preference', 'Employee Number Creation Preference')}</span>

					<span className={enableEmployeeNumberCreationDuringNewHireWarning ? style.banner_container : ''}>
						<Banner
							id={'banner'}
							message={t('EnableEmployeeNumberCreationDuringNewHireWarning', 'To disable this setting, please hire or remove any pending new hire with an employee number.')}
							status={Banner.Status.WARNING}
							show={enableEmployeeNumberCreationDuringNewHireWarning}
						/>
					</span>
					<span className={`${style.toggle_container} ${style.checkbox_align}`}>
						<ToggleSwitch
							toggleState={_employeeSettings.settings.enableEmployeeNumberCreationBeforeHire}
							onLabel={'On'}
							offLabel={'Off'}
							isDisabled={enableEmployeeNumberCreationDuringNewHireWarning}
							onChange={(e) => handleChange({ type: employeeSettingsActions.HANDLE_TOGGLE, key: 'enableEmployeeNumberCreationBeforeHire', value: e })}
						/>
						<p>
							{t('employee_number_creation_off', 'When off, an Employee Number cannot be created before an employee’s hire date')} <br />
							{t('employee_number_creation_on', 'When on, an Employee Number can be created at the start of the new hire process')}
						</p>
					</span>
				</section>
			</>
		);
	};

	const TooltipLabel = () => {
		return (
			<div style={{ display: 'flex', alignitems: 'center' }}>
				<span style={{ marginRight: '13px' }}>{t('max_character_Length_number_allowed', 'Maximum Character Length Allowed for Employee Number')}</span>
				<span>
					<ToolTip position={ToolTip.Positions.TOP}>
						<ToolTip.Anchor>
							<Info color={Colors.BLUE2} />
						</ToolTip.Anchor>
						<ToolTip.Body>
							<div className={style.message_width}>
								{t('Maximum_character_length_tooltip', 'The maximum character length can be between 6 and 10, depending on services included in your company.')}
								<br />
								<br /> {t('Employee_number_character_tooltip', 'This means that your employee numbers can have as many characters as set below.')}
							</div>
						</ToolTip.Body>
					</ToolTip>
				</span>
			</div>
		);
	};

	const renderFormattingOptions = () => {
		return (
			<>
				<section className={style.section_container}>
					<span className={style.section_title}>{t('FormatingOptions', 'Formatting Options')}</span>

					<span className={style.BoldDescription}>
						<span>{t('format_option_desc', 'Prior to making changes, consider any external integrations and employee number requirements.')}</span>
					</span>

					<span className={_employeeSettings.settings.allowAlphaNumericEmployeeNumber || _employeeSettings.settings.allowLeadingZeros ? style.banner_container : ''}>
						<Banner
							id={'banner'}
							message={t('AllowLettersandZerosWarning', 'Incrementation only applies to employee numbers without letters or leading zeros.')}
							status={Banner.Status.WARNING}
							show={_employeeSettings.settings.allowAlphaNumericEmployeeNumber || _employeeSettings.settings.allowLeadingZeros}
						/>
					</span>

					<div className={style.Employee_checkbox_container}>
						<div>
							<span className={style.employee_number}>
								<Checkbox
									id={'allowAlphaNumericEmployeeNumber'}
									checkedStatus={_employeeSettings.settings.allowAlphaNumericEmployeeNumber ? Checkbox.StatusTypes.CHECKED : Checkbox.StatusTypes.NONE}
									onChange={(e) => handleChange({ type: employeeSettingsActions.HANDLE_TOGGLE, key: 'allowAlphaNumericEmployeeNumber', value: e.checked })}
									label={t('AllowLetters_Used', 'Allow letters to be used in employee numbers.')}
								/>
							</span>

							<span className={`${style.toggle_container} ${style.checkbox_align}`}>
								<Checkbox
									checkedStatus={_employeeSettings.settings.allowLeadingZeros ? Checkbox.StatusTypes.CHECKED : Checkbox.StatusTypes.NONE}
									onChange={(e) => handleChange({ type: employeeSettingsActions.HANDLE_TOGGLE, key: 'allowLeadingZeros', value: e.checked })}
									label={t('AllowLeading_Zeros', 'Allow leading zeros to be used in employee numbers.')}
								/>
							</span>
						</div>

						<span className={style.employee_numbering_input_wrapper_formating}>
							<span className={style.employee_numbering_input_wrapper_formating}>
								<div className={style.input_container}>
									<InputField
										id={'employeeNumberMaxLength'}
										label={TooltipLabel()}
										type="number"
										value={String(_employeeSettings.settings.employeeNumberMaxLength)}
										errorMessage={_employeeSettings.errors.employeeNumberMaxLength ? ' ' : ''}
										onChange={(e) => handleChange({ type: employeeSettingsActions.HANDLE_TOGGLE, key: 'employeeNumberMaxLength', value: e })}
										isRequired={true}
									/>
									{_employeeSettings.errors.employeeNumberMaxLength && <div className={style.error_message}>{_employeeSettings.errors.employeeNumberMaxLength}</div>}
								</div>
							</span>
						</span>
					</div>
				</section>
				{horizontalSeparator()}
			</>
		);
	};
	const renderButtons = () => {
		function hasFormValuesChanged(currentSettings, savedSettings) {
			for (var p1 in currentSettings) {
				if (currentSettings.hasOwnProperty(p1)) {
					if (currentSettings[p1] !== savedSettings[p1]) {
						return false;
					}
				}
			}
			for (var p2 in savedSettings) {
				if (savedSettings.hasOwnProperty(p2)) {
					if (currentSettings[p2] !== savedSettings[p2]) {
						return false;
					}
				}
			}
			return true;
		}

		return (
			<section className={style.sticky_buttons}>
				<div>
					<hr className={style.Footerhorizontalline} />
					<Button type={Button.Types.SECONDARY} onClick={handleCancel}>
						{t('cancel', 'Cancel')}
					</Button>
					<Button
						type={Button.Types.SAVE}
						onClick={handleSubmit}
						actionType="submit"
						icon={Check}
						isDisabled={hasFormValuesChanged(_employeeSettings.settings, _employeeSettings.savedSettings) || !hasEditPriv}
					>
						{t('save', 'Save')}
					</Button>
				</div>
			</section>
		);
	};
	const renderModal = () => <IntegrationConsiderationsModal handleAPI={handleAPI} handleShowModal={handleShowModal} showModal={showModal} />;

	return (
		<>
			{isPageLoading && <LoadingSplashScreen pageLoad={true} />}
			{Object.keys(_employeeSettings.settings).length > 0 && (
				<article className={style.employee_setting_content}>
					{renderBanner()}
					{renderEmployeeNumbering()}
					{renderFormattingOptions()}
					{renderEmployeeNumberCreationPreference()}
					{renderButtons()}
					{renderModal()}
				</article>
			)}
		</>
	);
};

export default EmployeeNumberSettings;
